import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Chip,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Divider,
    LinearProgress,
    Stack
} from '@mui/material';

// project imports
import { setSelectedFunction, updateFunctionStatus, deleteFunction } from 'store/slices/lambdaFunctions';
import FunctionTestModal from './FunctionTestModal';

// icons
import {
    IconDots,
    IconTrash,
    IconEdit,
    IconCopy,
    IconChartLine,
    IconTestPipe,
    IconCode,
    IconClock,
    IconCpu,
    IconApi
} from '@tabler/icons-react';

// ==============================|| FUNCTION CARD ||============================== //

const FunctionCard = ({ function: func }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState(null);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [simulating, setSimulating] = useState(false);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleTest = () => {
        handleMenuClose();
        setTestModalOpen(true);
    };

    const handleEdit = () => {
        handleMenuClose();
        dispatch(setSelectedFunction(func));
        navigate(`/lambda/functions/${func.id}/edit`);
    };

    const handleDelete = () => {
        handleMenuClose();
        if (window.confirm(`Are you sure you want to delete ${func.name}?`)) {
            dispatch(deleteFunction(func.id));
        }
    };

    const handleDeploy = () => {
        setSimulating(true);
        // Simulate deployment
        setTimeout(() => {
            dispatch(updateFunctionStatus({ functionId: func.id, status: 'deployed' }));
            setSimulating(false);
        }, 2000);
    };

    const handleViewMetrics = () => {
        dispatch(setSelectedFunction(func));
        navigate(`/lambda/metrics/${func.id}`);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'deployed':
                return 'success';
            case 'deploying':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatMetric = (value, suffix = '') => {
        if (value === null || value === undefined) return 'N/A';
        return `${value}${suffix}`;
    };

    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4]
                    }
                }}
            >
                {simulating && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1
                        }}
                    />
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box flex={1}>
                            <Typography variant="h4" gutterBottom>
                                {func.name}
                            </Typography>
                            <Box display="flex" gap={1} mb={1}>
                                <Chip label={func.status} size="small" color={getStatusColor(func.status)} sx={{ fontWeight: 600 }} />
                                <Chip label={`v${func.version}`} size="small" variant="outlined" />
                            </Box>
                        </Box>
                        <IconButton size="small" onClick={handleMenuClick}>
                            <IconDots size={20} />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
                        {func.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Function Info */}
                    <Stack spacing={1.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconApi size={16} style={{ opacity: 0.6 }} />
                            <Typography variant="caption" color="textSecondary">
                                Endpoint:
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {func.endpoint}
                            </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                            <IconCpu size={16} style={{ opacity: 0.6 }} />
                            <Typography variant="caption" color="textSecondary">
                                Memory:
                            </Typography>
                            <Typography variant="caption">{func.config.memory}</Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                            <IconClock size={16} style={{ opacity: 0.6 }} />
                            <Typography variant="caption" color="textSecondary">
                                Timeout:
                            </Typography>
                            <Typography variant="caption">{func.config.timeout}s</Typography>
                        </Box>
                    </Stack>

                    {/* Metrics */}
                    {func.status === 'deployed' && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Performance (24h)
                                </Typography>
                                <Box display="flex" justifyContent="space-between" mt={1}>
                                    <Box textAlign="center">
                                        <Typography variant="h5">{formatMetric(func.metrics.total_executions_24h)}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Executions
                                        </Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography variant="h5" color={func.metrics.success_rate > 95 ? 'success.main' : 'warning.main'}>
                                            {formatMetric(func.metrics.success_rate, '%')}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Success Rate
                                        </Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <Typography variant="h5">{formatMetric(func.metrics.avg_latency_ms, 'ms')}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Avg Latency
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                    {func.status === 'deployed' ? (
                        <Box display="flex" gap={1} width="100%">
                            <Button size="small" variant="outlined" startIcon={<IconTestPipe size={16} />} onClick={handleTest} fullWidth>
                                Test
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IconChartLine size={16} />}
                                onClick={handleViewMetrics}
                                fullWidth
                            >
                                Metrics
                            </Button>
                        </Box>
                    ) : func.status === 'failed' ? (
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<IconTestPipe size={16} />}
                            onClick={handleDeploy}
                            fullWidth
                            disabled={simulating}
                        >
                            Retry Deploy
                        </Button>
                    ) : func.status === 'deploying' ? (
                        <Button size="small" variant="outlined" fullWidth disabled>
                            Deploying...
                        </Button>
                    ) : null}
                </CardActions>

                {/* Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={handleEdit}>
                        <IconEdit size={16} style={{ marginRight: 8 }} />
                        Edit Function
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            navigator.clipboard.writeText(func.endpoint);
                        }}
                    >
                        <IconCopy size={16} style={{ marginRight: 8 }} />
                        Copy Endpoint
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            navigate(`/lambda/functions/${func.id}/code`);
                        }}
                    >
                        <IconCode size={16} style={{ marginRight: 8 }} />
                        View Code
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                        <IconTrash size={16} style={{ marginRight: 8 }} />
                        Delete Function
                    </MenuItem>
                </Menu>
            </Card>

            {/* Test Modal */}
            <FunctionTestModal open={testModalOpen} onClose={() => setTestModalOpen(false)} function={func} />
        </>
    );
};

export default FunctionCard;
